<script setup>
import { sortAgeGroup, swalError } from '@/helper';
import { get_intervention, get_intervention_activites, update_intervention } from '@/services/interventions';
import { get_terms } from '@/services/terms';
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute()
const isLoading = ref(true)
const intervention = ref({
  name: '',
  code: '',
  trait_id: '',
  document: '',
  document_name: '-',
  package_id: null
})
const trait_config = ref({
  name: ''
})
const ageGroupOption = ref([])
const intervention_term = ref()
const intervention_response = ref({})
onMounted(async ()=>{
  intervention.value.trait_id = route.params.trait_id
  let response = await get_intervention(route.params.id)
  if (response.error){
    if (!response.isRedirecting) swalError(response.message || 'Failed to load intervention')
    return
  }
  
  intervention_response.value = response.result.data.data
  intervention.value['id'] = response.result.data.data.id
  intervention.value.name = response.result.data.data.attributes.name
  intervention.value.code = response.result.data.data.attributes.code
  intervention.value.document_name = response.result.data.data.attributes.document_name
  intervention.value.package_id = response.result.data.data.relationships?.package?.data?.id || null
  intervention.value.trait_id = response.result.data.data.relationships?.trait?.data?.id || route.params.trait_id

  trait_config.value.name = response.result.data.included.find(item => item.type == 'trait').attributes.name
  intervention_term.value = response.result.data.meta.term_relationship
  let age_group_response = await get_terms('age_group_int')
  if (age_group_response.error){
    if (!age_group_response.isRedirecting) swalError(age_group_response.message || 'Failed to load age groups')
    return
  }
  ageGroupOption.value = generateOption(age_group_response.result.data.data)
  parseActivity(response.result.data.included)

  isLoading.value = false
})

function parseActivity(items){
  let temp_activities = items.map((item)=>{
    if (item.type == "intervention_activity"){
      return {
        title: item.attributes.title,
        id: item.id,
        _destroy: false
      }
    }
  })

  temp_activities = temp_activities.filter(item => item) // remove null 
  activities.value = temp_activities
}

function generateOption(reponse){
  reponse = reponse.map(item => {
    return {
      valueProp: item.relationships.term_taxonomy.data.id,
      label: item.attributes.name,
      disabled: false
    }
  })
  return sortAgeGroup(reponse)
  // console.log("🚀 ~ file: characterCard.vue:39 ~ generateOption ~ traitOptions.value:", traitOptions.value)
}

const isChange = ref(false)
function validateReport(event) {
  const selectedFile = event.target.files[0]; // Get the first selected file
  intervention.value.document = selectedFile
  isChange.value = true
  if (intervention.value.document ) {
    // console.log("🚀 ~ file: download_report.vue:20 ~ validateReport ~ uploadedFile.value:", uploadedFile.value)
  }
}

const isSubmitting = ref(false)
async function updateIntervention(){
  let isPass = checkForm()
  if (isPass.error){
    return swalError(isPass.msg)
  }
  isSubmitting.value = true
  const interventionParams = {
    intervention_obj:{
      ...intervention.value,
      intervention_activities_attributes: activities.value
    },
    term_relationship:{
      id: intervention_term.value.id,
      term_taxonomy_id: intervention_term.value.term_taxonomy_id
    }
  }
  if (!isChange.value){
    delete interventionParams.intervention_obj.document
  }
  let response = await update_intervention(intervention.value.id, interventionParams)
  if (response.error){
    if (!response.isRedirecting) swalError(response.message || 'Failed to update intervention', true)
    isSubmitting.value = false
    return
  }
  intervention.value.document_name = response.result.data.data.attributes.document_name
  intervention_response.value = response.result.data.data
  Swal.fire({
    title: 'Updated',
    text: 'Intervention has been updated',
    icon: 'success'
  })
  isSubmitting.value = false
}

function checkForm(){
  let isPass = {error: false, msg: ''}
  if (intervention.value.name == ''){
    isPass.msg = 'Form is not completed'
    isPass.error = true
  }else if(temp_activity.value != ''){
    isPass.msg = 'Check your activity description'
    isPass.error = true
  }else if(intervention_term.value.term_taxonomy_id == null){
    isPass.msg = 'Check your age group option'
    isPass.error = true
  }
  return isPass
}

const temp_activity = ref('')
const activities = ref([])
function addActivity(){
  activities.value.push({title: temp_activity.value, id: '', _destroy: false})
  temp_activity.value = ''
}

function removeActivity(index, indicatorObj, indicators){
  if (indicatorObj.id == ''){
    activities.value.splice(index, 1)
  }else{
    // console.log("🚀 ~ file: editAssessment.vue:57 ~ removeIndicator ~ indicatorObj:", indicatorObj)
    activities.value[index]._destroy = true
    // console.log("🚀 ~ file: editAssessment.vue:60 ~ removeIndicator ~ indicator:", indicator)
    // indicator._destroy = true
    indicators = indicators.map(item =>{
      if (item === indicatorObj){
        item._destroy = 'true'
      }
      return item
    })
  }
}

async function downloadDoc(){
  if (!intervention_response.value.attributes?.document) return
  
  try {
    const response = await fetch(intervention_response.value.attributes.document, {
      headers: {
        'Authorization': localStorage.getItem('uid')
      }
    })
    
    if (!response.ok) throw new Error('Download failed')
    
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = intervention.value.document_name || 'intervention_document.pdf'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Download error:', error)
    swalError('Failed to download file')
  }
}

function removeFile(){
  let inputEl = document.getElementById('intervention_file')
  inputEl.value = null
  intervention.value.document = ""
  intervention.value.document_name = ""
}
</script>

<template>
  <div class="container-fluid">
    <Breadcrumbs 
      main="Interventions" 
      :title="trait_config.name + ' - Edit Intervention'"
      :mainRoute="{name: 'package-intervention-list'}"
      v-bind="intervention.package_id && { titleRoute: {name: 'intervention-list', params: {package_id: intervention.package_id, trait_id: intervention.trait_id}} }"
    />
    
    <LoadingCard v-if="isLoading" />
    <template v-else>
      <!-- Page title -->
      <div class="d-flex align-items-center">
        <div>
          <h4 class="mb-0">Edit intervention for {{ trait_config.name }}</h4>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-9">
          <div class="col-12 card">
            <div class="card-body">
              <div class="form-items mt-3">
                <div class="row">
                  <div class="col-3 d-none">
                    <label class="form-label" for="code">Code</label>
                    <input v-model="intervention.code" class="form-control" id="code" type="text" placeholder="Code" required>
                  </div>
                  <div class="col-12">
                    <label class="form-label" for="intervention_description">Intervention Description</label>
                    <textarea rows="4" v-model="intervention.name"
                      id="intervention_description" placeholder="Intervention Description" 
                      class="form-control"
                      required
                    ></textarea>
                  </div>
                </div>
    
                <div class="mt-4">
                  <label class="form-label" for="age">Intervention document</label>
                  <div class="row">
                    <div class="col-md-9">
                      <div class="d-flex align-items-center">
                        <input 
                          class="form-control" 
                          id="intervention_file"
                          type="file" 
                          aria-label="dna report" 
                          required=""
                          @change="validateReport"
                          accept="application/pdf"
                        >
                        <button class="ms-2 btn icon-btn btn-light-danger" @click="removeFile()">
                          <vue-feather type="trash" size="1rem"/>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="mt-2 d-flex align-items-center">
                    <p class="mb-0">
                      <span class="text-secondary">Uploaded file :</span>
                      {{ intervention.document_name }}
                    </p>
                    <template v-if="intervention.document_name">
                      <button class="ms-2 btn icon-btn btn-light-success" @click="downloadDoc()">
                        <vue-feather type="download" size="1rem"/>
                      </button>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          <!-- Activity -->
          <div class="col-12 card">
            <div class="card-header pb-2 pt-3">
              <h5 class="mb-0">Activity</h5>
              <p class="mb-0 text-secondary">Add activity list for intervention</p>
            </div>
            <div class="card-body">
              <form @submit.prevent="addActivity()" class="row mt-3">
                <div class="col-8">
                  <input class="form-control" 
                    type="text" 
                    placeholder="Activity Description" required
                    v-model="temp_activity"
                  >
                </div>
                <div class="col-2">
                  <button type="submit" class="btn-rounded-icon p-0 btn btn-success"
                  >
                    <vue-feather type="plus"></vue-feather>
                  </button>
                </div>
              </form>
              <!-- items -->
              <div v-for="(activity, index) in activities" v-auto-animate>
                <div v-if="!activity._destroy" class="row mt-2" >
                  <div class="col-8">
                    <input class="form-control" 
                      type="text" 
                      placeholder="Activity Description" required
                      v-model="activity.title"
                    >
                  </div>
                  <div class="col-2">
                    <button class="btn-rounded-icon p-0 btn btn-danger"
                      @click="removeActivity(index, activity, activities)"
                    >
                      <vue-feather type="x"></vue-feather>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Submit & Age Group -->
        <div class="col-3">
          <div class="card">
            <div class="card-body">
              <div class="row mb-3">
                <label class="form-label" for="age_group_from">Age Group</label>
                <template v-if="intervention_term">
                  <multiselect 
                    class="ms-3"
                    v-model="intervention_term.term_taxonomy_id" 
                    placeholder="Select age group"
                    :options="ageGroupOption"
                    valueProp="valueProp"
                    searchable="true"
                  ></multiselect>
                </template>
              </div>
              <button class="btn btn-light-success"
                @click="updateIntervention()"
                :disabled="isSubmitting"
              >
                <div v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <template v-else>
                  Update Intervention
                </template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>